import { ActionContextType, ActionsContext, MxAction } from '../../Shell/Pandora/model/action';

import type { IComponentController } from 'angular';
import template from './dashboard-control-panel.component.html';
import type { I18n } from '../../i18n/mx-internationalization';
import { DashboardPageComponent, DashboardPageController } from '../dashboard-page/dashboard-page.component';
import type { MxActionManagerService } from '../../Shell/Pandora/services/mx-action-manager-service';
import type { AutoRefreshInterval, DashboardService } from '../dashboard.service';
import type { NotificationService } from '../../App/services/mx-notification-service';
import type { DataModelCacheService } from '../../Shell/PandoraSolutionBuilderAgent/services/data-model-cache.service';
import type { ConfigService } from '../../App/services/mx-config-service';
import type { WizardService } from '../../Shell/PandoraSolutionBuilderAgent/services/wizard-service';
import { WizardMode } from '../../Shell/PandoraSolutionBuilderAgent/model/enumerations';
import type { SidePanelService } from '../../Shell/Pandora/services/mx-side-panel-service';
import type { EntityService } from '../../Shell/PandoraSolutionBuilderAgent/services/entity-service';
import { DashboardExportWizardController } from '../dashboard-export-wizard/dashboard-export-wizard.component';
import dashboardExportWizardTemplate from '../dashboard-export-wizard/dashboard-export-wizard.component.html';
import { DashboardPrintWizardController } from '../dashboard-print-wizard/dashboard-print-wizard.component';
import dashboardPrintWizardTemplate from '../dashboard-print-wizard/dashboard-print-wizard.component.html';
import type { AnyObject } from '../../Types';
import type { DashboardData } from '../dashboard-export-base64/dashboard-export-base64.component';
import type { UserProfileService } from '../../App/services/user-profile-service';
import type { MxNavigationService } from '../../Navigation/mx-navigation-service';
import { DisplayMode } from '../../Shell/Pandora/model/display-modes';
import { EventCodes } from '../../App/model/event-codes';
import type { EventService } from '../../App/services/event-service';

interface AutoRefreshOption {
	Title: string;
	Value: AutoRefreshInterval;
}

export class DashboardControlPanelController implements IComponentController {
	page!: DashboardPageController;
	actions: MxAction[] = [];
	autoRefreshOptions: AutoRefreshOption[] = [];

	static readonly $inject = [
		'mx.internationalization',
		'mx.shell.ActionManagerService',
		'mx.sb.Dashboard.Service',
		'mx.shell.NotificationService',
		'mx.SolutionBuilderAgent.DataModelCacheService',
		'mx.shell.Config',
		'mx.SolutionBuilderAgent.WizardService',
		'mx.shell.SidePanelService',
		'mx.SolutionBuilderAgent.EntityService',
		'mx.shell.UserProfileService',
		'mx.shell.NavigationService',
		'mx.shell.EventService',
	];

	constructor(
		private readonly i18n: I18n,
		private readonly actionManagerService: MxActionManagerService,
		private readonly dashboardService: DashboardService,
		private readonly notificationService: NotificationService,
		private readonly dataModelCacheService: DataModelCacheService,
		private readonly configService: ConfigService,
		private readonly wizardService: ReturnType<typeof WizardService>,
		private readonly sidePanelService: ReturnType<typeof SidePanelService>,
		private readonly entityService: EntityService,
		private readonly userProfileService: UserProfileService,
		private readonly navigationService: MxNavigationService,
		private readonly eventService: EventService
	) {
		this.autoRefreshOptions = [
			{
				Title: this.i18n.get('shell.common.off'),
				Value: 0,
			},
			{
				Title: this.i18n.getFormatted('shell.dashboards.autoRefresh.everyMinutes', {
					minutes: 30,
				}),
				Value: 30,
			},
			{
				Title: this.i18n.get('shell.dashboards.autoRefresh.everyHour'),
				Value: 60,
			},
			{
				Title: this.i18n.getFormatted('shell.dashboards.autoRefresh.everyHours', {
					hours: 2,
				}),
				Value: 120,
			},
			{
				Title: this.i18n.getFormatted('shell.dashboards.autoRefresh.everyHours', {
					hours: 4,
				}),
				Value: 240,
			},
			{
				Title: this.i18n.get('shell.dashboards.autoRefresh.everyDay'),
				Value: 1440,
			},
		];
	}

	$onInit() {
		this.getActions().then((actions) => (this.actions = actions));
	}

	$onDestroy() {
		this.dataModelCacheService.enable();
	}

	private async initActions() {
		this.actions = await this.getActions();
	}

	async onSaveClick() {
		await this.page.disableConfigurationMode();
		await this.initActions();
		this.dataModelCacheService.enable();
	}

	private async showConfirmation(confirmationTitle: string, confirmationMessage: string): Promise<boolean> {
		try {
			await this.notificationService.confirm(confirmationTitle, confirmationMessage);
			return true;
		} catch (e) {
			return false;
		}
	}

	async getActions() {
		const dashboardActions: MxAction[] = [];

		const actionContext = new ActionsContext([], []);
		const actions = this.actionManagerService.filterActions(await this.actionManagerService.getActions(actionContext, [ActionContextType.None]));
		const editOriginal = actions.find((action) => action.name.startsWith('UUX.Client.Dashboard.Edit'));
		const fullScreenOriginal = actions.find((action) => action.name.startsWith('UUX.Client.Dashboard.FullScreen'));
		const switchBackOriginal = actions.find((action) => action.name.startsWith('UUX.Client.Dashboard.SwitchBack'));
		const publishActionOriginal = actions.find((action) => action.name.startsWith('UUX.Client.Dashboard.PublishLayout'));
		const removeCustomizationActionOriginal = actions.find((action) => action.name.startsWith('UUX.Client.Dashboard.RevertPublishedLayout'));
		const removePersonalizationActionOriginal = actions.find((action) => action.name.startsWith('UUX.Client.Dashboard.RemovePersonalization'));
		const deleteActionOriginal = actions.find((action) => action.name.startsWith('UUX.Client.Dashboard.Delete'));
		const forceReloadActionOriginal = actions.find((action) => action.name.startsWith('UUX.Client.Dashboard.ForceReload'));
		const shareActionOriginal = actions.find((action) => action.name.startsWith('UUX.Client.Dashboard.Share'));
		const sendReportActionOriginal = actions.find((action) => action.name.startsWith('UUX.Client.Dashboard.SendReport'));
		const exportActionOriginal = actions.find((action) => action.name.startsWith('UUX.Client.Dashboard.Export'));
		const printActionOriginal = actions.find((action) => action.name.startsWith('UUX.Client.Dashboard.Print'));

		const addAction = (action: MxAction, exec: () => void, popover?: string, onPopoverClose?: () => void) => {
			const execute = async () => {
				const isConfirmed =
					!action.serviceOperation?.ConfirmationMessage ||
					(await this.showConfirmation(action.customConfirmationTitle ?? action.title, (action.serviceOperation?.ConfirmationMessage || '') as string));

				if (isConfirmed) {
					exec();
				}
			};

			dashboardActions.push(
				this.createAction({
					name: action.name,
					title: action.title,
					icon: action.icon,
					execute,
					popover: popover,
					onPopoverClose: onPopoverClose,
				})
			);
		};

		const dashboard = this.page.dashboard;
		if (switchBackOriginal) {
			addAction(switchBackOriginal, () => this.dashboardService.dashboardLookOperation(dashboard.navigationId));
		}

		if (publishActionOriginal && this.dashboardService.hasPersonalization(dashboard)) {
			addAction(publishActionOriginal, async () => {
				await this.dashboardService.publishDashboard(dashboard);
				this.page.reloadPage();
			});
		}

		if (removeCustomizationActionOriginal && dashboard.isCustomized) {
			addAction(removeCustomizationActionOriginal, async () => {
				await this.dashboardService.removeCustomization(dashboard);
				this.page.reloadPage();
			});
		}

		if (removePersonalizationActionOriginal && this.dashboardService.hasPersonalization(dashboard)) {
			addAction(removePersonalizationActionOriginal, async () => {
				await this.dashboardService.removePersonalization(dashboard);
				this.page.reloadPage();
			});
		}

		if (forceReloadActionOriginal) {
			const isRefreshPopoverHidden = this.dashboardService.isRefreshPopoverHidden();

			addAction(
				forceReloadActionOriginal,
				async () => {
					await this.dataModelCacheService.resetCache();
					this.page.reloadPage();
				},
				isRefreshPopoverHidden ? undefined : this.i18n.getFormatted('shell.dashboards.cacheHint', this.configService.get('dataSourcesCacheLifetime')),
				isRefreshPopoverHidden ? undefined : () => this.dashboardService.setRefreshPopoverHidden()
			);
		}

		if (exportActionOriginal) {
			addAction(exportActionOriginal, async () => {
				await this.notificationService.customDialog({
					controllerAs: 'vm',
					controller: DashboardExportWizardController,
					template: dashboardExportWizardTemplate,
					locals: {
						dashboard,
					},
					bindToController: true,
				});
			});
		}

		if (printActionOriginal) {
			addAction(printActionOriginal, async () => {
				await this.notificationService.customDialog({
					controllerAs: 'vm',
					controller: DashboardPrintWizardController,
					template: dashboardPrintWizardTemplate,
					locals: {
						dashboard,
					},
					bindToController: true,
				});
			});
		}

		if (editOriginal) {
			addAction(editOriginal, async () => {
				this.page.enableConfigurationMode();
				this.dataModelCacheService.disable();
			});
		}

		if (sendReportActionOriginal) {
			const dashboardData: DashboardData = {
				filters: dashboard.filters.values,
				title: dashboard.title ?? '',
				navigationId: dashboard.navigationId,
			};
			addAction(sendReportActionOriginal, async () => {
				await this.showWizard({
					navigationId: dashboard.navigationId,
					widgetId: '3ff3ba7f-d0ea-c8f2-b86a-08dd04e1297b',
					widgetName: this.i18n.get('solutionBuilderAgent.common.sendReport'),
					actionName: sendReportActionOriginal.name,
					presetFields: {
						Context: {
							DashboardData: dashboardData,
						},
					},
				});
			});
		}

		if (shareActionOriginal && (!dashboard.owner || dashboard.isOwner)) {
			addAction(shareActionOriginal, async () => {
				await this.showWizard({
					navigationId: dashboard.navigationId,
					widgetId: 'a54d3c0e-204f-ef11-0486-ec63d7895110',
					widgetName: this.i18n.get('solutionBuilderAgent.common.share'),
					actionName: shareActionOriginal.name,
				});
			});
		}

		if (deleteActionOriginal) {
			const user = await this.userProfileService.get();
			deleteActionOriginal.customConfirmationTitle = this.i18n.getFormatted('solutionBuilderAgent.common.deleteTemplate', dashboard.title);
			if (user.isDevMode || (dashboard.owner && (dashboard.isOwner || user.isAdmin)))
				addAction(deleteActionOriginal, async () => {
					const parentItem = (await this.navigationService.getNavigationItem(dashboard.navigationId))?.parent;
					await this.dashboardService.deleteDashboard(dashboard);
					const index = parentItem?.children?.findIndex((n) => n.id === dashboard.navigationId) ?? -1;
					await this.navigationService.setNavigationItemById(parentItem?.children?.[index - 1]?.id ?? parentItem?.id ?? this.navigationService.navItems[0].id);
					this.navigationService.removeItem(dashboard.navigationId, parentItem);
				});
		}

		if (fullScreenOriginal) {
			addAction(fullScreenOriginal, () => {
				this.page.setDisplayMode(DisplayMode.BigScreen);
				this.eventService.dispatchEvent(EventCodes.TrackTelemetry, {
					eventName: 'Dashboard fullscreen mode enabled',
				});
			});
		}

		return dashboardActions;
	}

	private async showWizard({
		navigationId,
		widgetId,
		widgetName,
		actionName,
		presetFields,
	}: {
		navigationId: string;
		widgetId: string;
		widgetName: string;
		actionName: string;
		presetFields?: AnyObject;
	}) {
		const objDetails = await this.entityService.getFragmentDescriptorsInBatch('PDRNavigationItemClassBase', navigationId);
		if (objDetails) {
			const navObjectId = objDetails.ObjectId;

			const wizardView = this.wizardService.getWizardView({
				widgetId: widgetId,
				wizardMode: WizardMode.Default,
				name: widgetName,
				displayName: widgetName,
				selectedObjects: [
					{
						_id: navObjectId,
						_type: 'PDRNavigationItemType',
						_displayName: this.page.dashboard.title,
					},
				],
				actionName: actionName,
				presetFields,
			});

			// @ts-expect-error no types
			wizardView.id = Date.now();
			this.sidePanelService.addView(wizardView);
		}
	}

	private createAction({
		name,
		title,
		icon,
		execute,
		popover,
		onPopoverClose,
	}: {
		name: string;
		title: string;
		icon: string;
		popover?: string;
		execute: () => void;
		onPopoverClose?: () => void;
	}) {
		const action = new MxAction(name, title, icon);
		action.popover = popover;
		if (onPopoverClose) {
			action.onPopoverClose = onPopoverClose;
		}
		action.controller = function () {
			return {
				execute,
			};
		};
		return action;
	}

	async onAutoRefreshOptionChanged() {
		await this.page.setAutoRefresherInterval(this.page.dashboard.autoRefreshInterval);
		this.eventService.dispatchEvent(EventCodes.TrackTelemetry, {
			eventName: 'Dashboard auto-refresh enabled',
			intervalHours: this.page.dashboard.autoRefreshInterval / 60,
		});
	}
}

export const DashboardControlPanelComponent = {
	name: 'mxDashboardControlPanel',
	controller: DashboardControlPanelController,
	controllerAs: 'vm',
	template,
	require: {
		page: `^${DashboardPageComponent.name}`,
	},
};
